import { FC, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  ColumnDef,
  ExpandedState,
  getExpandedRowModel,
  Row,
} from '@tanstack/react-table';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { faLocationDot } from '@fortawesome/pro-regular-svg-icons/faLocationDot';
import { faUser } from '@fortawesome/pro-regular-svg-icons/faUser';
import { faGraduationCap } from '@fortawesome/pro-regular-svg-icons/faGraduationCap';
import { Table } from '@fe-core-ui/src/components/Table/Table';
import { useTable } from '@fe-core-ui/src/components/Table/useTable';
import { TableRowExpandBtn } from '@fe-core-ui/src/components/Table/TableRowExpandBtn';
import { CourseThumbnail } from '@fe-core-ui/src/components/CourseThumbnail';
import { FaIcon, Tooltip } from '@fe-learner-common';
import { AvatarGroup } from '@fe-learner-common/components/Avatar/AvatarGroup';
import { EmptyStateWrapper } from '@fe-learner-common/components/EmptyState';
import { toLocaleDate } from '@fe-learner-common/utils/date';
import { TSessionChild, TSessionChildEvent } from '@api/io/session.children.io';
import { TW_ROOT } from '@utils/constants';

type SessionChildrenTableProps = {
  items: TSessionChild[];
  isLoading: boolean;
};

const getEventRows = (item: TSessionChild): TSessionChildEvent[] =>
  item.events?.length
    ? item.events
    : [{ startDate: item.startDate, location: item.location }];

const isExpandable = (item: TSessionChild): boolean =>
  getEventRows(item).length > 1 || item.instructors.length > 1;

const toAvatarUser = (instructor: TSessionChild['instructors'][number]) => ({
  alt: instructor.name,
  src: instructor.thumbnail ?? undefined,
});

const subRowColStyle = (colIndex: number): React.CSSProperties => ({
  position: 'absolute',
  left: `var(--col-${colIndex}-left)`,
  width: `var(--col-${colIndex}-width)`,
});

const SubRowContent: FC<{ row: Row<TSessionChild> }> = ({ row }) => {
  const item = row.original;
  const events = getEventRows(item);
  const maxRows = Math.max(events.length, item.instructors.length);

  return (
    <div className="lf:flex lf:flex-col lf:bg-neutral-50">
      {Array.from({ length: maxRows }, (_, i) => {
        const event = events[i];
        const instructor = item.instructors[i];

        return (
          <div
            key={i}
            className="lf:relative lf:py-3"
            style={{ minHeight: 42 }}
          >
            <div
              className="lf:flex lf:items-center lf:gap-2 lf:px-3 lf:text-neutral-500"
              style={subRowColStyle(2)}
            >
              {event?.startDate && (
                <>
                  <FaIcon icon={faCalendar} width={14} height={14} />
                  <span className="lf:whitespace-nowrap">
                    {toLocaleDate(event.startDate)}
                  </span>
                </>
              )}
            </div>
            <div
              className="lf:flex lf:items-center lf:gap-2 lf:overflow-hidden lf:px-3 lf:text-neutral-500"
              style={subRowColStyle(3)}
            >
              {event?.location && (
                <>
                  <FaIcon icon={faLocationDot} width={14} height={14} />
                  <span className="lf:overflow-hidden lf:text-ellipsis">
                    {event.location}
                  </span>
                </>
              )}
            </div>
            <div
              className="lf:flex lf:items-center lf:gap-2 lf:px-3 lf:text-neutral-500"
              style={subRowColStyle(5)}
            >
              {instructor && (
                <>
                  <AvatarGroup size="md" users={[toAvatarUser(instructor)]} />
                  <span>{instructor.name}</span>
                </>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const SessionChildrenTable: FC<SessionChildrenTableProps> = ({
  items,
  isLoading,
}) => {
  const intl = useIntl();
  const [expanded, setExpanded] = useState<ExpandedState>({});

  const columns: ColumnDef<TSessionChild>[] = useMemo(
    () => [
      {
        id: 'expand',
        header: () => <span className="l-table-head-shrink" />,
        size: 48,
        cell: ({ row }) => {
          if (!row.getCanExpand()) return null;
          return (
            <TableRowExpandBtn
              row={row}
              label={intl.formatMessage(
                { id: 'courses.projectSelection.sessions.expand' },
                { name: row.original.title },
              )}
            />
          );
        },
      },
      {
        id: 'title',
        header: () => (
          <span className="l-table-head-grow">
            {intl.formatMessage({
              id: 'courses.projectSelection.sessions.column.name',
            })}
          </span>
        ),
        cell: ({ row }) => (
          <div className="lf:flex lf:items-center lf:gap-3">
            <CourseThumbnail thumbnail={row.original.thumbnail} />
            <span className="lf:text-label-lg lf:text-neutral-700">
              {row.original.title}
            </span>
          </div>
        ),
      },
      {
        id: 'dates',
        header: intl.formatMessage({
          id: 'courses.projectSelection.sessions.column.dates',
        }),
        size: 200,
        cell: ({ row }) => {
          const events = getEventRows(row.original);

          if (events.length > 1) {
            return (
              <div className="lf:flex lf:items-center lf:gap-2 lf:text-neutral-600">
                <FaIcon icon={faCalendar} width={14} height={14} />
                <span>
                  {intl.formatMessage(
                    { id: 'courses.projectSelection.sessions.eventCount' },
                    { count: events.length },
                  )}
                </span>
              </div>
            );
          }

          if (!events[0]?.startDate) return null;
          return (
            <div className="lf:flex lf:items-center lf:gap-2 lf:text-neutral-600">
              <FaIcon icon={faCalendar} width={14} height={14} />
              <span className="lf:whitespace-nowrap">
                {toLocaleDate(events[0].startDate)}
              </span>
            </div>
          );
        },
      },
      {
        id: 'location',
        header: intl.formatMessage({
          id: 'courses.projectSelection.sessions.column.location',
        }),
        size: 200,
        cell: ({ row }) => {
          const locations = new Set(
            getEventRows(row.original)
              .map((event) => event.location)
              .filter(Boolean),
          );
          if (!locations.size) return null;

          return (
            <div className="lf:flex lf:items-center lf:gap-2 lf:text-neutral-600">
              <FaIcon icon={faLocationDot} width={14} height={14} />
              <span>
                {locations.size > 1
                  ? intl.formatMessage({
                      id: 'courses.projectSelection.sessions.multipleLocations',
                    })
                  : [...locations][0]}
              </span>
            </div>
          );
        },
      },
      {
        id: 'seatsAvailable',
        header: intl.formatMessage({
          id: 'courses.projectSelection.sessions.column.seats',
        }),
        size: 120,
        cell: ({ row }) => {
          const seats = row.original.seatsAvailable;
          if (seats == null) return null;
          return (
            <div className="lf:flex lf:items-center lf:gap-2 lf:text-neutral-600">
              <FaIcon icon={faUser} width={14} height={14} />
              <span>{seats}</span>
            </div>
          );
        },
      },
      {
        id: 'instructors',
        header: intl.formatMessage({
          id: 'courses.projectSelection.sessions.column.instructors',
        }),
        size: 200,
        cell: ({ row }) => {
          const instructors = row.original.instructors;
          if (!instructors.length) return null;

          if (instructors.length > 1) {
            return (
              <Tooltip
                container={document.getElementById(TW_ROOT) as HTMLElement}
                text={instructors.map((i) => i.name).join(', ')}
              >
                <div className="lf:flex lf:items-center lf:gap-2">
                  <AvatarGroup
                    size="md"
                    users={instructors.map(toAvatarUser)}
                  />
                  <span className="lf:text-neutral-600">
                    {intl.formatMessage(
                      {
                        id: 'courses.projectSelection.sessions.instructorCount',
                      },
                      { count: instructors.length },
                    )}
                  </span>
                </div>
              </Tooltip>
            );
          }

          return (
            <div className="lf:flex lf:items-center lf:gap-2 lf:text-neutral-600">
              <AvatarGroup size="md" users={[toAvatarUser(instructors[0])]} />
              <span>{instructors[0].name}</span>
            </div>
          );
        },
      },
    ],
    [intl],
  );

  const table = useTable({
    data: items,
    columns,
    isLoading,
    tableOptions: {
      enableSorting: false,
      state: { expanded },
      onExpandedChange: setExpanded,
      getRowCanExpand: (row) => isExpandable(row.original),
      getExpandedRowModel: getExpandedRowModel(),
    },
  });

  return (
    <EmptyStateWrapper
      icon={<FaIcon icon={faGraduationCap} className="lf:text-neutral-550" />}
      label={intl.formatMessage({
        id: 'courses.projectSelection.sessions.empty.label',
      })}
      description={intl.formatMessage({
        id: 'courses.projectSelection.sessions.empty.description',
      })}
      condition={isLoading || items.length > 0}
    >
      <Table
        table={table}
        isLoading={isLoading}
        renderSubComponent={({ row }) => <SubRowContent row={row} />}
      />
    </EmptyStateWrapper>
  );
};
